import { useEffect, useSyncExternalStore } from "react";
import Cookies from "js-cookie";
import { API_PROJECT_DESIGNATIONS } from "../config/Endpoints";
import { getApi } from "../utils/api";
import { isOutsidePerformanceLens } from "../v2/lib/performance-lens/designation-scope.ts";

let snapshot = { options: [], loading: false, error: null, loaded: false };
let pendingRequest = null;
const listeners = new Set();

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const publish = (next) => {
  snapshot = next;
  listeners.forEach((listener) => listener());
};

const getSnapshot = () => snapshot;

export const loadProjectDesignationOptions = () => {
  if (snapshot.loaded) return Promise.resolve(snapshot.options);
  if (pendingRequest) return pendingRequest;

  publish({ ...snapshot, loading: true, error: null });
  pendingRequest = getApi(API_PROJECT_DESIGNATIONS, Cookies.get("accessToken"))
    .then((response) => {
      const data = response.data;
      if (!Array.isArray(data)) throw new Error("Invalid project designation response");
      const options = data.map(({ id, value, label, locationType, legacyValue, performanceRoles }) => ({
        id, value, label, locationType, legacyValue,
        performanceRoles: isOutsidePerformanceLens(value) ? [] : Array.isArray(performanceRoles) ? performanceRoles : [],
      }));
      publish({ options, loading: false, error: null, loaded: true });
      return options;
    })
    .catch((error) => {
      publish({ options: [], loading: false, error, loaded: false });
      return [];
    })
    .finally(() => { pendingRequest = null; });
  return pendingRequest;
};

export const useProjectDesignationOptions = () => {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => { loadProjectDesignationOptions(); }, []);
  return state;
};

export const getProjectDesignationOptions = () => snapshot.options;

export const invalidateProjectDesignationOptions = () => {
  publish({ options: [], loading: false, error: null, loaded: false });
  void loadProjectDesignationOptions();
};

export const findProjectDesignationOption = (designation, options = snapshot.options) => {
  const normalized = String(designation ?? "").trim().toLowerCase().replace(/\s+/g, "_");
  return options.find((option) =>
    option.value.toLowerCase() === normalized ||
    option.legacyValue?.toLowerCase() === normalized ||
    option.label.toLowerCase().replace(/\s+/g, "_") === normalized,
  );
};

export const getProjectDesignationLabel = (designation, options = snapshot.options) => {
  if (designation == null || String(designation).trim() === "") return "—";
  return findProjectDesignationOption(designation, options)?.label ||
    String(designation).trim().replace(/_/g, " ");
};
